import { logger } from '~/features/logging/logger';

import { getEnrollment } from '../controllers/get-enrollment';
import { getEnrollmentProgressRaw } from '../controllers/get-enrollment-progress-raw';
import { EnrollmentProgressRaw } from '../models/enrollment-progress';
import { Enrollment, EnrollmentState } from '../models/program-enrollment';

const MAX_RETRIES = 10;

const ACTIVE_ENROLLMENT_STATES: ReadonlySet<EnrollmentState> = new Set<EnrollmentState>([
  'ENROLLED',
  'STATIC_ACCESS',
  'GRADUATED',
]);

export async function pollForEnrollment({
  programKey,
  retryCount = 0,
  requireActiveState = false,
}: {
  programKey: string;
  retryCount?: number;
  /**
   * When re-enrolling an `UNENROLLED` program the stale enrollment record still exists with progress,
   * so the default existence check resolves immediately. Set this to keep polling until the state has
   * actually flipped to an active state, otherwise navigation can race ahead of re-enrollment.
   */
  requireActiveState?: boolean;
}): Promise<{ enrollment: Enrollment; enrollmentProgress: EnrollmentProgressRaw } | null> {
  try {
    const [enrollment, enrollmentProgress] = await Promise.all([
      getEnrollment({ programKey }),
      getEnrollmentProgressRaw(programKey),
    ]);

    if (enrollment && enrollmentProgress.data) {
      const isActive = ACTIVE_ENROLLMENT_STATES.has(enrollment.state);
      if (!requireActiveState || isActive) {
        return { enrollment, enrollmentProgress };
      }
    }
  } catch (error) {
    logger.error('Error polling for enrollment', error);
  }

  if (retryCount < MAX_RETRIES) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return await pollForEnrollment({ programKey, retryCount: retryCount + 1, requireActiveState });
  } else {
    return null;
  }
}
